import React, { FunctionComponent, useEffect, useMemo, useState } from 'react';
import {
  SynchronizationStatusContext,
  SynchronizationStatusContextValue
} from 'record-it-components/context/SynchronizationStatusContext';
import { StrictReactNode } from 'record-it-components/types/StrictReactNode';
import { prepareProps } from 'record-it-components/utils/propUtils';
import { AppEntityManager } from '../classes/entities/AppEntityManager';
import { AppEntityManagerApiAdapter } from '../classes/entities/AppEntityManagerApiAdapter';
import { StorageClearer } from '../classes/StorageClearer/StorageClearer';
import {
  EntityManagerContext,
  EntityManagerContextValue
} from '../context/EntityManagerContext';
import { useAuth } from '../hooks/useAuth';
import { useInitializable } from '../hooks/useInitializable/useInitializable';
import { testingHelper } from '../testingHelper';

export const AppEntityManagerContextProvider: FunctionComponent<{
  children: StrictReactNode;
}> = React.memo((props) => {
  const { children } = prepareProps({ props });
  const [synchronizationStatus, setSynchronizationStatus] =
    useState<SynchronizationStatusContextValue>({
      inProgress: false,
      itemsToSynchronizeInfos: []
    });

  const { authenticationData } = useAuth();

  const { initializable, createNewInstance, removeInstance } = useInitializable(
    {
      createInstance: () => {
        const newEntityManager = new AppEntityManager(
          new AppEntityManagerApiAdapter()
        );
        testingHelper.entityManager = newEntityManager;

        return {
          entityManager: newEntityManager,
          init: async () => {
            await newEntityManager.init();

            newEntityManager.entitySynchronization.bindItemsToSynchronizeInfos(
              (infos) => {
                setSynchronizationStatus((status) => ({
                  ...status,
                  itemsToSynchronizeInfos: infos.map((i) => ({
                    id: i.entityId,
                    entityName: i.entityName,
                    failed: i.failed
                  }))
                }));
              }
            );

            newEntityManager.entitySynchronization.bindIsSynchronizing(
              (isSynchronizing) => {
                setSynchronizationStatus((status) => ({
                  ...status,
                  inProgress: isSynchronizing
                }));
              }
            );
          },
          flush: () => newEntityManager.flush(),
          destroy: () => newEntityManager.destroy()
        };
      }
    }
  );

  useEffect(() => {
    const beforeDisposable = StorageClearer.registerBeforeClearTask(() =>
      removeInstance()
    );
    const afterDisposable = StorageClearer.registerAfterClearTask(() =>
      createNewInstance()
    );

    return () => {
      beforeDisposable.dispose();
      afterDisposable.dispose();
    };
  }, [createNewInstance, removeInstance]);

  useEffect(() => {
    if (initializable) {
      if (authenticationData) {
        initializable.entityManager.enableSynchronization();
      }
    }

    return () => initializable?.entityManager.disableSynchronization();
  }, [authenticationData, initializable]);

  const contextValue: EntityManagerContextValue | null = useMemo(() => {
    return initializable
      ? { entityManager: initializable.entityManager }
      : null;
  }, [initializable]);

  if (!contextValue) {
    return <div>Intializing EntityManager..</div>;
  }

  return (
    <EntityManagerContext.Provider value={contextValue}>
      <SynchronizationStatusContext.Provider value={synchronizationStatus}>
        {children}
      </SynchronizationStatusContext.Provider>
    </EntityManagerContext.Provider>
  );
});
